import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useErrorHandler } from '../../services/errorHandler';
import { useForm } from '../../hooks/useForm';
import { authSchemas } from '../../utils/validationSchemas';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import ModernLanguageSwitcher from '../../components/common/ModernLanguageSwitcher';
import { EyeIcon, EyeSlashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const { login, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isRTL } = useLanguage();
  const errorHandler = useErrorHandler();
  const signupSuccess = useMemo(() => new URLSearchParams(location.search).get('signupSuccess') === 'true', [location.search]);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: authSchemas(t).login,
    onSubmit: async (values) => {
      try {
        await login(values.email, values.password);
        errorHandler.showSuccess(t('success.login', 'Login successful!'));
        // Navigation will be handled by the useEffect above
      } catch (error: any) {
        // Use the localized message from API response if available
        if (error?.response?.data) {
          const apiError = error.response.data;
          // Choose message based on current language (isRTL indicates Arabic)
          const message = isRTL && apiError.messageAr 
            ? apiError.messageAr 
            : apiError.message || t('auth.invalidCredentials', 'Invalid email or password');
          errorHandler.showWarning(message);
        } else {
          // Handle any unexpected errors
          errorHandler.showWarning(t('auth.loginFailed', 'Login failed. Please try again'));
        }
      }
    }
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  return (
    <div className="w-full max-w-md mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Back to Home Button */}
      <div className="mb-6 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors duration-200"
        >
          <ArrowLeftIcon className={`h-4 w-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
          {t('common.back', 'Back')} {t('nav.home', 'Home')}
        </Link>
        <div className="hidden sm:block">
          <ModernLanguageSwitcher />
        </div>
      </div>

      <div className="text-center mb-8">
        {signupSuccess && (
          <div className="mb-4 p-3 rounded bg-green-50 text-green-700 text-sm">
            {t('auth.signupSuccessMessage', 'Your account is ready. Please sign in.')}
          </div>
        )}
        <div className="flex justify-center mb-4">
          <div className="h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">RF</span>
          </div>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
          {t('auth.login')}
        </h2>
        <p className="mt-2 text-xs sm:text-sm text-gray-600">
          {t('auth.loginSubtitle', 'Sign in to your account to continue')}
        </p>
      </div>

      <form onSubmit={form.handleSubmit} className="space-y-5 sm:space-y-6">
        <Input
          {...form.getFieldProps('email')}
          label={t('auth.email')}
          type="email"
          autoComplete="email"
          placeholder={t('auth.email', 'Email')}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('auth.password')}
            <span className="text-red-500 ml-1">*</span>
          </label>
          <div className="relative">
            <input
              {...form.getFieldProps('password')}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder={t('auth.password', 'Password')}
              className={`w-full px-3 py-2 ${isRTL ? 'pl-10 pr-3' : 'pr-10'} border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${
                form.errors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50' : 'hover:border-gray-400'
              }`}
            />
            <button
              type="button"
              className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center`}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <EyeIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </div>
          {form.errors.password && (
            <p className="text-sm text-red-600 font-medium flex items-center gap-1 mt-1">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {form.errors.password}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="remember-me" className={`${isRTL ? 'mr-2' : 'ml-2'} block text-sm text-gray-900`}>
              {t('auth.rememberMe')}
            </label>
          </div>

          <div className="text-sm">
            <Link
              to="/forgot-password"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>
        </div>

        <Button
          type="submit"
          loading={form.isSubmitting}
          className="w-full"
          disabled={form.isSubmitting}
        >
          {form.isSubmitting ? (
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>{t('auth.signingIn', 'Signing in...')}</span>
            </div>
          ) : (
            t('auth.signIn')
          )}
        </Button>
      </form>

      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">
              {t('auth.noAccount', "Don't have an account?")}
            </span>
          </div>
        </div>

        <div className="mt-6">
          <Link
            to="/register"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary-600 hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500"
          >
            {t('auth.signUp')}
          </Link>
        </div>

        <div className="mt-4 sm:hidden flex justify-center">
          <ModernLanguageSwitcher />
        </div>
      </div>
    </div>
  );
};

export default Login;
